import type { Notification, NotificationPage, NotificationParent, NotificationSubtype, NotificationTab } from 'types/notification';

const PAGE_COUNT = 20;

/** ⚠️ Meteor 콜백이라 안 올 수도 있다. 목록을 기다리다 멈추는 것보다 빈 페이지로 끝내는 게 낫다. */
const FETCH_TIMEOUT_MS = 5000;

/** 서버에 '제거 예정' 으로 남아 있는 옛 알림. 그릴 카드가 없어 받아봐야 빈 줄만 남는다. */
const EXCLUDE_UPDATE = { subtype: { $ne: 'update' } };

const FILTER: Record<NotificationTab, Record<string, unknown>> = {
  all: { ...EXCLUDE_UPDATE },
  // parentCategory 가 없던 옛 문서도 alert 다.
  alert: { $or: [{ parentCategory: null }, { parentCategory: 'alert' }], ...EXCLUDE_UPDATE },
  purchase: { parentCategory: 'purchase' },
  system: { parentCategory: 'system' },
};

/** 그릴 카드가 있는 것만 남긴다 — 못 그리면 빈 줄로 자리만 먹는다. */
const RENDERED: Record<NotificationParent, NotificationSubtype[]> = {
  alert: ['missedCall', 'makeFriend'],
  purchase: ['purchaseCoin', 'supplyCoin', 'supplyLuckybox', 'compensateCoin', 'purchaseTimeBuff'],
  system: ['client'],
};

export type ClientContent = {
  titleKey: string;
  contentKey: string;
  buttonKey?: string;
  link?: 'TabHome' | 'AppReview';
  photoUrl?: string;
};

/** system/client 알림의 고정 문구. 여기 없는 category 는 그릴 게 없다. */
export const CLIENT_CONTENT: Record<string, ClientContent> = {
  welcome: {
    titleKey: 'noti_page_title_welcome',
    contentKey: 'bermuda_chat_welcome',
    buttonKey: 'goto_home',
    link: 'TabHome',
    photoUrl: '/imgweb/notification/img_team_noti_welcome.jpg',
  },
  askreview: {
    titleKey: 'noti_page_title_review',
    contentKey: 'ask_review',
    buttonKey: 'goto_write_review',
    link: 'AppReview',
    photoUrl: '/imgweb/notification/img_team_noti_review.jpg',
  },
  report: { titleKey: 'noti_page_title_report_result', contentKey: 'msg_reporting_result' },
  banreport: { titleKey: 'team_bermuda', contentKey: 'reporter_notipage_2', photoUrl: '/imgweb/notification/img_team_noti_report.jpg' },
  minorreport: { titleKey: 'team_bermuda', contentKey: 'profile_reject_noti_1' },
  profilereport: { titleKey: 'team_bermuda', contentKey: 'profile_reject_noti_1' },
  reportsubmitted: { titleKey: 'team_bermuda', contentKey: 'reporter_notipage_1', photoUrl: '/imgweb/notification/img_team_noti_report.jpg' },
  uncomfortableWarning: { titleKey: 'team_bermuda', contentKey: 'first_warning_title' },
  uncomfortableRestrict: { titleKey: 'team_bermuda', contentKey: 'usage_restriction_title' },
};

function toMs(value: unknown): number {
  const ms = new Date(value as string).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function toNumber(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
}

function shape(raw: unknown, baseTs: number): Notification | null {
  const doc = raw as Record<string, unknown>;
  const id = String(doc?._id ?? '');
  const parentCategory = (doc?.parentCategory as NotificationParent) ?? 'alert';
  const subtype = String(doc?.subtype ?? '') as NotificationSubtype;
  const category = String(doc?.category ?? '');

  if (!id || !RENDERED[parentCategory]?.includes(subtype)) return null;
  if (subtype === 'client' && !CLIENT_CONTENT[category]) return null;

  const createdTime = toMs(doc.createdTime);
  return {
    id,
    parentCategory,
    subtype,
    category,
    createdTime,
    unread: createdTime > baseTs,
    senderUid: doc.senderUid ? String(doc.senderUid) : undefined,
    status: doc.status ? String(doc.status) : undefined,
    amount: toNumber(doc.amount),
    count: toNumber(doc.count),
    billingCompletedAt: doc.billingCompletedAt ? toMs(doc.billingCompletedAt) : undefined,
    reportNick: doc.reportNick ? String(doc.reportNick) : undefined,
  };
}

/**
 * 보낸 사람을 한 번에 받아 붙인다.
 *
 * 탈퇴·차단으로 못 찾은 상대의 알림은 그릴 게 없어 목록에서 뺀다 — 남겨 두면 빈 줄이 된다.
 * getUserInfoList 는 못 찾은 uid 를 빼고 돌려주므로 결과 길이가 요청보다 짧을 수 있다.
 */
async function fillSenders(items: Notification[]): Promise<Notification[]> {
  const uids = Array.from(new Set(items.map((item) => item.senderUid).filter(Boolean) as string[]));
  if (uids.length === 0) return items;

  const found: UserInfo[] = await FriendsDataManager.getUserInfoList(uids);
  const byUid = new Map(found.filter(Boolean).map((user) => [user.fbUid, user]));

  return items
    .filter((item) => !item.senderUid || byUid.has(item.senderUid))
    .map((item) => (item.senderUid ? { ...item, sender: byUid.get(item.senderUid) } : item));
}

type RawPage = { list?: unknown[]; token?: unknown } | null;

function callGetList(tab: NotificationTab, token: unknown): Promise<{ page: RawPage; ok: boolean }> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ page: null, ok: false }), FETCH_TIMEOUT_MS);
    const done = (page: RawPage, ok: boolean) => {
      clearTimeout(timer);
      resolve({ page, ok });
    };

    try {
      NotificationDao.getList(FILTER[tab], { token, count: PAGE_COUNT }, (error, result) => {
        if (error || !result) {
          BLog.error('[notificationService] getList failed:', error);
          done(null, false);
          return;
        }
        done(result, true);
      });
    } catch (error) {
      BLog.error('[notificationService] getList threw:', error);
      done(null, false);
    }
  });
}

/**
 * 한 페이지를 받아 화면이 쓰는 모양으로 편다.
 *
 * 조회가 실패하면 보냈던 토큰을 그대로 돌려준다 — done 을 세우면 그 탭이 영영 멈춘다.
 */
export async function fetchPage(tab: NotificationTab, token: unknown, baseTs: number): Promise<NotificationPage> {
  const { page, ok } = await callGetList(tab, token);
  if (!ok || !page) return { items: [], token, done: false };

  const raw = Array.isArray(page.list) ? page.list : [];
  const shaped = raw.map((item) => shape(item, baseTs)).filter(Boolean) as Notification[];
  return { items: await fillSenders(shaped), token: page.token ?? null, done: !page.token };
}

/**
 * 읽음 기준선. 이 시각보다 나중에 온 알림이 unread 다.
 *
 * 화면에 들어온 순간 한 번만 읽어야 한다 — 스크롤 도중에 바뀌면 밑줄이 따라 움직인다.
 */
export function readUnreadBaseline(): number {
  return toNumber(log?.lastNotiNewMarkClear) ?? 0;
}

/**
 * 기준선을 지금으로 올린다.
 *
 * NotificationDataManager.clearNotificationNewMark 는 log.notiNewMark 가 true 일 때만 갱신하는데
 * 그 값을 세우는 setNotificationNewMark 를 부르는 곳이 없어 기준선이 첫 방문 뒤로 안 움직인다.
 * 그래서 여기서 직접 쓴다.
 */
export function markAllRead(): void {
  if (!fbRefMyUserInfo) return;
  fbRefMyUserInfo
    .child('log/lastNotiNewMarkClear')
    .set(firebase.database.ServerValue.TIMESTAMP)
    .then(() => {
      log.lastNotiNewMarkClear = Date.now();
    })
    .catch((error: unknown) => BLog.error('[notificationService] markAllRead failed:', error));
}

export type TabCache = { items: Notification[]; token: unknown; done: boolean };

export type ListCache = {
  /** 이 목록을 담아 둔 history 항목. 같은 항목으로 돌아온 경우에만 되살린다. */
  key: string;
  tabs: Partial<Record<NotificationTab, TabCache>>;
  tab: NotificationTab;
  scrollTop: number;
};

let cache: ListCache | null = null;

/**
 * 상점·프로필에 다녀오면 react-router 는 같은 history 항목으로 되돌아오므로 key 가 그대로다.
 * 알림 버튼으로 새로 들어오면 push 라 key 가 새로 생겨 목록을 처음부터 받는다.
 *
 * 읽기만 하고 지우지 않는다 — StrictMode 가 마운트를 두 번 태워도 같은 답이 나와야 한다.
 */
export function readCache(key: string): ListCache | null {
  return cache && cache.key === key ? cache : null;
}

export function storeCache(next: ListCache): void {
  cache = next;
}

export function clearCache(): void {
  cache = null;
}

export function sendClickLog(action: string, data?: Record<string, unknown>): void {
  const params = { ...data, fbUid: myinfo.fbUid, action };
  BTracker.sendBLog('noti_click', params, true);
  ba('noti_click', undefined, params);
}
